import { type RouteObject } from 'react-router'
import type { JobsFeature } from '@/features/jobs'
import {
  createDraftJobViewLoader,
  createJobDetailsTabLoader,
  createJobDetailsViewLoader,
  createRedirectFromLegacyRouteLoader,
  createTaxInfoGatheringViewLoader,
  createConsultationInfoGatheringViewLoader,
  createTaxPrepOrganizerViewLoader,
  createMeasureOneViewLoader,
  createMeasureOneConnectionViewLoader,
  createMeasureOneDocumentsLoader,
} from '@/features/jobs/job-route-loaders'
import type {
  SelectWorkspaceByIdFn,
  WaitForAuthResolutionFn,
} from '@/features/iam'

/**
 * Options for {@link createRoutes}.
 */
export interface CreateJobRoutesOptions {
  jobs: JobsFeature
  waitForAuthResolution: WaitForAuthResolutionFn
  selectWorkspace: SelectWorkspaceByIdFn
}

/**
 * Creates routes for the account management feature.
 */
export function createRoutes(options: CreateJobRoutesOptions): RouteObject[] {
  return [
    {
      path: '/jobs',
      lazy: {
        Component: () =>
          import('@/features/jobs/list/components/AllJobsView/AllJobsViewContainer').then(
            (r) => r.default,
          ),
      },
    },
    {
      path: '/jobs/:id',
      lazy: {
        Component: () =>
          import('@/features/jobs/details/JobDetailsView').then(
            (r) => r.default,
          ),
      },
      loader: createJobDetailsViewLoader(options),
      children: [
        {
          index: true,
          loader: createJobDetailsTabLoader(options, 'overview'),
          lazy: {
            Component: () =>
              import('@/features/jobs/details/JobDetailsView/OverviewTab').then(
                (r) => r.default,
              ),
          },
        },
        {
          path: 'messages',
          lazy: {
            Component: () =>
              import('@/features/jobs/details/JobDetailsView/MessagesTab').then(
                (r) => r.default,
              ),
          },
        },
        {
          path: 'docs',
          lazy: {
            Component: () =>
              import('@/features/jobs/details/JobDetailsView/DocsTab').then(
                (r) => r.default,
              ),
          },
        },
        {
          path: 'billing',
          loader: createJobDetailsTabLoader(options, 'billing'),
          lazy: {
            Component: () =>
              import('@/features/jobs/details/JobDetailsView/BillingTab').then(
                (r) => r.default,
              ),
          },
        },
      ],
    },
    {
      path: '/jobs/:id/tax-info-gathering/:selectedItemId?',
      lazy: {
        Component: () =>
          import('@/features/jobs/details/tax-type/info-gathering/TaxInfoGathering').then(
            (r) => r.default,
          ),
      },
      loader: createTaxInfoGatheringViewLoader(options),
    },
    {
      id: 'measureOne',
      path: '/jobs/:id/tax-info-gathering/measure-one',
      lazy: {
        Component: () =>
          import('@/features/jobs/details/tax-type/info-gathering/measure-one/MeasureOneWizardView').then(
            (r) => r.default,
          ),
      },
      loader: createMeasureOneViewLoader(options),
      children: [
        {
          path: 'experience-type',
          lazy: {
            Component: () =>
              import('@/features/jobs/details/tax-type/info-gathering/measure-one/MeasureOneWizardView/ExperienceTypeView').then(
                (r) => r.default,
              ),
          },
        },
        {
          path: 'connection',
          loader: createMeasureOneConnectionViewLoader(options),
          lazy: {
            Component: () =>
              import('@/features/jobs/details/tax-type/info-gathering/measure-one/MeasureOneWizardView/ConnectionView').then(
                (r) => r.default,
              ),
          },
        },
        {
          path: 'documents',
          loader: createMeasureOneDocumentsLoader(options),
          lazy: {
            Component: () =>
              import('@/features/jobs/details/tax-type/info-gathering/measure-one/MeasureOneWizardView/SelectDocumentsView').then(
                (r) => r.default,
              ),
          },
        },
      ],
    },
    {
      path: '/jobs/:id/consultation-info-gathering',
      lazy: {
        Component: () =>
          import('@/features/jobs/details/consultation-type/info-gathering/ConsultationInfoGathering').then(
            (r) => r.default,
          ),
      },
      loader: createConsultationInfoGatheringViewLoader(options),
    },
    {
      path: '/jobs/:id/tax-prep-organizer',
      lazy: {
        Component: () =>
          import('@/features/jobs/details/tax-type/tax-prep-organizer/TaxPrepOrganizerView').then(
            (r) => r.default,
          ),
      },
      loader: createTaxPrepOrganizerViewLoader(options),
    },
    {
      path: '/jobs/:id/onboarding',
      lazy: {
        Component: () =>
          import('@/features/jobs/drafting/components/DraftJobView').then(
            (r) => r.default,
          ),
      },
      loader: createDraftJobViewLoader(options),
    },
    // Legacy routes from old portal
    {
      path: '/project/:id',
      loader: createRedirectFromLegacyRouteLoader(),
      element: null,
    },
    {
      path: '/project/:id/edit',
      loader: createRedirectFromLegacyRouteLoader(),
      element: null,
    },
  ]
}
